import React, { useState, useMemo } from "react";
import { Table, DatePicker, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Truck } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetInventoryReportPurchases } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const PurchasesReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["purchases-summary-report", startDateStr, endDateStr],
    queryFn: () =>
      GetInventoryReportPurchases({
        start_date: startDateStr,
        end_date: endDateStr,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const amount = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_amount || 0),
      0,
    );
    const paid = reportData.reduce(
      (sum: number, item: any) => sum + (item.paid_amount || 0),
      0,
    );
    const due = reportData.reduce(
      (sum: number, item: any) => sum + (item.due_amount || 0),
      0,
    );
    return [
      {
        title: "Total Purchase Invoice Sum",
        value: formatLKR(amount),
        colorClass: "text-blue-600",
      },
      {
        title: "Total Paid Amount",
        value: formatLKR(paid),
        colorClass: "text-emerald-600",
      },
      {
        title: "Remaining Balance Due",
        value: formatLKR(due),
        colorClass: due > 0 ? "text-rose-600 font-extrabold" : "text-gray-400",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Purchase Date",
      dataIndex: "purchase_date",
      key: "purchase_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-medium">
          {dayjs(v).format("YYYY-MM-DD")}
        </span>
      ),
    },
    {
      title: "Invoice / PO",
      dataIndex: "invoice_no",
      key: "invoice_no",
      render: (v: string, record: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-bold text-gray-800">
            Inv: {v || "Draft"}
          </span>
          {record.po_no && (
            <span className="text-[10px] text-gray-400">
              PO: {record.po_no}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
      render: (v: string) => (
        <span className="text-xs font-semibold text-gray-750">{v}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => {
        const color = v === "Completed" ? "success" : "default";
        return (
          <Tag
            color={color}
            className="rounded-full font-bold text-[10px] uppercase"
          >
            {v}
          </Tag>
        );
      },
    },
    {
      title: "Total Invoice",
      dataIndex: "total_amount",
      key: "total_amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-gray-800">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Discount",
      dataIndex: "discount_amount",
      key: "discount_amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-rose-600 font-medium">
          -LKR {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Paid",
      dataIndex: "paid_amount",
      key: "paid_amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-emerald-600 font-bold">
          LKR {v.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Balance Due",
      dataIndex: "due_amount",
      key: "due_amount",
      align: "right" as const,
      render: (v: number) => (
        <span
          className={`text-xs font-extrabold ${v > 0 ? "text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-100" : "text-gray-400"}`}
        >
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date Period
        </span>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            dates && dates[0] && dates[1] && setDateRange([dates[0], dates[1]])
          }
          allowClear={false}
          className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Purchases Summary"
      description="Overview of inbound vendor procurements, order status, total costs, payments, and balances due."
      icon={<Truck className="w-6 h-6 text-blue-600" />}
      backPath="/system/inventory-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) =>
            record.id || record.invoice_no || Math.random().toString()
          }
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
